// CSV選択出力によりCSVをダウンロードするためのロジックをまとめたHooks
import { toast } from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';

import { components } from '../../schema/data-crud';
import { datacrudApiClient as client } from '../../utils/apiClient';
import { useSelectedOrCurrentYear } from '../useYears';
import { CustomToast } from './CustomToast';

// -----------------------------------------------------------------------------
// CRUD Operation Functions
// -----------------------------------------------------------------------------

// 国表CSV出力のジョブをリクエストする
const postOaCsvRequest = async (
  request: components['schemas']['OaCsvCreateRequest']
): Promise<components['schemas']['OaCsvCreateResponse']> => {
  const { data, error } = await client.POST('/oa-csv', {
    body: request,
  });

  if (error) {
    throw new Error(`Failed to POST /Oa-csv/csv \n message: ${error.message}`);
  }

  return data;
};

// 国表CSV出力のジョブ一覧を取得する
const getOaCsvRequestList = async (
  year: string
): Promise<components['schemas']['OaCsvGetJobListResponse']> => {
  const { data, error } = await client.GET('/oa-csv', {
    params: {
      query: {
        year,
      },
    },
  });

  if (error) {
    throw new Error(`Failed to GET /oa-csv \n message: ${error.message}`);
  }

  return data;
};

// 国表CSV出力のダウンロードURLを取得する
const getOaCsvDownloadUrl = async (
  requestId: string
): Promise<components['schemas']['OaCsvGetDownloadUrlResponse']> => {
  const { data, error } = await client.GET('/oa-csv/{requestId}', {
    params: {
      path: {
        requestId,
      },
    },
  });

  if (error) {
    throw new Error(`Failed to GET /oa-csv/{requestId} \n message: ${error.message}`);
  }

  return data;
};

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

// 国表CSV出力のリクエストを処理する
export const useOaCsvRequest = () => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: postOaCsvRequest,
    onMutate: () => {
      const toastId = toast.loading('CSV出力のリクエストを処理中です...');
      return { toastId };
    },
    onSuccess: (data, _, context) => {
      toast.success(CustomToast, {
        id: context?.toastId,
        duration: 8000,
      });
      console.log(`Accepted oa-csv create request: id=${data.requestId}`);
    },
    onError: (error, _, context) => {
      toast.error('CSV出力のリクエストに失敗しました', {
        id: context?.toastId,
      });
      console.log(error);
    },
  });
  return { request: mutateAsync, isPending: isLoading };
};

// CSV選択出力のジョブ一覧を取得する
export const useOaCsvJobList = () => {
  const year = useSelectedOrCurrentYear();
  const { data } = useQuery({
    queryKey: ['oa-csv'],
    queryFn: async () => {
      return await getOaCsvRequestList(year);
    },
  });
  return data;
};

// CSV選択出力のダウンロードURLを取得しダウンロードを実行する
export const useOaCsvDownload = (requestId: string) => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async () => getOaCsvDownloadUrl(requestId),
    onMutate: () => {
      const toastId = toast.loading('ダウンロードを準備中です...');
      return { toastId };
    },
    onSuccess: (data, _, context) => {
      toast.success('ダウンロードを開始します', {
        id: context?.toastId,
      });
      window.location.href = data.url;
    },
    onError: (error, _, context) => {
      console.log(error);
      toast.error('ダウンロードURLの取得に失敗しました', {
        id: context?.toastId,
      });
    },
  });
  return { download: mutateAsync, isPending: isLoading };
};
